import React, {
  FunctionComponent,
  useState,
  SyntheticEvent,
  useEffect,
  useRef,
} from "react";
import { ChoiceList, Button } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../helpers";

interface CCXPDropdownProps {
  value: string | number;
  options: { label: string; value: string | number }[];
  label: string;
  onUpdate: (value: number | string) => void;
}

const CCXPDropdown: FunctionComponent<CCXPDropdownProps> = ({
  value,
  options,
  label,
  onUpdate,
}) => {
  const [open, setOpen] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const menuRef = useRef<HTMLDivElement>(null);
  const t = useTranslate();

  const currentOption = options.find(o => o.value === value)?.label || label;

  const clickHandler = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;

    if (menuRef && menuRef.current && !menuRef.current.contains(target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (open) {
      document.addEventListener("click", clickHandler);
    } else {
      document.removeEventListener("click", clickHandler);
    }
  }, [open]);

  return (
    <div className="CCXPDropdown" ref={menuRef}>
      <button
        className={`CCXPDropdown__button ${
          value && !open ? "CCXPDropdown__button--withValue" : ""
        }`}
        onClick={(): void => setOpen(!open)}
      >
        {currentOption}{" "}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          className={`ds-u-margin-left--1 ${
            open ? "CCXPDropdown__rotate" : ""
          }`}
        >
          <path
            fill="#323A45"
            fillRule="nonzero"
            d="M11.681 15.87l-5.549-5.39a.435.435 0 0 1 0-.624l.743-.727a.457.457 0 0 1 .637 0L12 13.48l4.488-4.35a.457.457 0 0 1 .637 0l.743.726a.435.435 0 0 1 0 .625l-5.55 5.39a.457.457 0 0 1-.637 0z"
          />
        </svg>
      </button>
      <div
        className={`CCXPDropdown__menu ${
          open ? "CCXPDropdown__menu--open" : ""
        }`}
      >
        <div className="CCXPDropdown__menuButtonContainer">
          <button
            className="CCXPDropdown__closeMenuButton"
            onClick={(): void => setOpen(false)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              preserveAspectRatio="xMidYMid meet"
              focusable="false"
            >
              <path
                fillRule="nonzero"
                d="M13.6 12l5.258-5.257a.485.485 0 0 0 0-.687l-.914-.914a.485.485 0 0 0-.687 0L12 10.4 6.743 5.142a.485.485 0 0 0-.687 0l-.914.914a.485.485 0 0 0 0 .687L10.4 12l-5.258 5.257a.485.485 0 0 0 0 .687l.914.914c.19.19.497.19.687 0L12 13.6l5.257 5.258c.19.19.497.19.687 0l.914-.914a.485.485 0 0 0 0-.687L13.6 12z"
              ></path>
            </svg>
          </button>
        </div>
        <ChoiceList
          className="ds-u-margin--0 ds-u-padding-right--6"
          label=""
          choices={options.map(o => ({
            ...o,
            checked: currentValue == o.value,
          }))}
          name={label}
          type="radio"
          onChange={(e: SyntheticEvent<HTMLInputElement>): void => {
            setCurrentValue(e.currentTarget.value);
          }}
        />
        <div className="CCXPDropdown__menuButtonContainer">
          <Button
            className="ds-c-button--primary-alt ds-u-margin-top--2"
            onClick={(): void => {
              onUpdate(currentValue);
              setOpen(false);
            }}
          >
            {t("pharm_selection.apply")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CCXPDropdown;
